import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetExperienceProfile } from '../utils/experience';

function TipsForProfile(profile) {
  if (profile.id === 'standalone') {
    return {
      lead: 'Nice. You are already in Home Screen mode.',
      steps: [
        'After you create your codename, tap Enable alerts in chat.',
        'That is how Conga and sleeping gas poke this phone when Funhouse needs you.',
      ],
      footnote: 'Keep this icon. Safari tabs on iPhone cannot keep the line alive the same way.',
    };
  }

  if (profile.id === 'ios-needs-homescreen') {
    return {
      lead: 'Best booth experience on iPhone: play from the Home Screen.',
      steps: [
        'Tap Share in Safari (the square with the arrow).',
        'Scroll and tap Add to Home Screen, then Add.',
        'Open Riddler from the new icon (not the Safari tab).',
        'After sign-in, tap Enable alerts so Conga can reach you.',
      ],
      footnote: 'Without Home Screen mode, iPhone will not show Funhouse push alerts.',
    };
  }

  if (profile.id === 'android-chrome') {
    return {
      lead: 'Chrome on Android can alert you from a normal tab.',
      steps: [
        'Optional: Add to Home Screen (Chrome menu → Install app / Add to Home screen) for a cleaner booth layout.',
        'After you sign in, tap Enable alerts when Funhouse offers it.',
        'Allow notifications when Chrome asks. Leave the app when you join the Conga so pokes can land.',
      ],
      footnote: 'You do not need Home Screen mode for Android push, but it feels better at the booth.',
    };
  }

  if (profile.id === 'desktop-push') {
    return {
      lead: 'This browser can do Funhouse alerts.',
      steps: [
        'After you sign in, tap Enable alerts in chat.',
        'Allow notifications when the browser asks.',
        'Keep the Funhouse tab open in the background while you wait in line.',
      ],
      footnote: 'Phone Home Screen mode is still best if you walk the floor.',
    };
  }

  return {
    lead: 'You can still play. Conga shoulder-taps may not work on this browser.',
    steps: [
      'Create a codename and keep Funhouse in front when your ticket is up.',
      'If you have an iPhone or Android, open this site there for a smoother booth run.',
    ],
    footnote: null,
  };
}

export default function SetupTips() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => GetExperienceProfile());
  const [installEvent, setInstallEvent] = useState(null);
  const [installBusy, setInstallBusy] = useState(false);
  const tips = TipsForProfile(profile);

  useEffect(() => {
    const RefreshProfile = () => setProfile(GetExperienceProfile());
    const OnVisibility = () => {
      if (document.visibilityState === 'visible') RefreshProfile();
    };
    window.addEventListener('focus', RefreshProfile);
    document.addEventListener('visibilitychange', OnVisibility);
    return () => {
      window.removeEventListener('focus', RefreshProfile);
      document.removeEventListener('visibilitychange', OnVisibility);
    };
  }, []);

  useEffect(() => {
    const OnPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };
    window.addEventListener('beforeinstallprompt', OnPrompt);
    return () => window.removeEventListener('beforeinstallprompt', OnPrompt);
  }, []);

  const handleInstall = async () => {
    if (!installEvent || installBusy) return;
    setInstallBusy(true);
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
    } catch {
      /* player dismissed / unsupported */
    } finally {
      setInstallEvent(null);
      setInstallBusy(false);
    }
  };

  return (
    <div className="disclosure-screen">
      <div className="disclosure-glow" aria-hidden="true" />
      <div className="disclosure-pattern" aria-hidden="true" />

      <main className="disclosure-panel setup-panel">
        <p className="brand-eyebrow">Before the Funhouse</p>
        <h1 className="setup-title">{profile.title}</h1>
        <p className="disclosure-lead">{tips.lead}</p>

        <ol className="setup-steps">
          {tips.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        {tips.footnote && (
          <p className="setup-footnote">{tips.footnote}</p>
        )}

        {installEvent && (
          <button
            type="button"
            className="btn-primary"
            onClick={handleInstall}
            disabled={installBusy}
          >
            {installBusy ? 'Opening install…' : 'Install Funhouse'}
          </button>
        )}

        <button
          type="button"
          className="btn-primary btn-play"
          onClick={() => navigate('/auth')}
        >
          Continue
        </button>
      </main>
    </div>
  );
}
